/**
 * Static mock data + config for the CRM pages, transcribed faithfully from the
 * handover prototype (docs/crm-handover/prototype/public/index.html). The CRM is
 * a clickable dummy UI on top of the support app — there is no backend yet, so
 * every screen renders from the fixtures and config below.
 *
 * Colours here carry status meaning (stage, risk, score) and are kept consistent
 * across Board, Analytics, Customer Success, Demo Tracker, Opportunities, etc.
 */

/* ─── Stages ──────────────────────────────────────────────────────────────── */

export interface Stage {
  key: string
  label: string
  /** Stage accent colour (hex) — kanban header, donut, funnel. */
  color: string
  /** Forecast weight: share of deal value counted toward weighted forecast. */
  weight: number
  /** Terminal/closed stage — hidden from the board by default. */
  terminal: boolean
}

export const STAGES: Stage[] = [
  { key: 'auto_new_lead', label: 'Auto New Lead', color: '#2563EB', weight: 0.05, terminal: false },
  { key: 'qualification', label: 'Qualification', color: '#3B82F6', weight: 0.1, terminal: false },
  { key: 'price_enquired', label: 'Price Enquired', color: '#A855F7', weight: 0.2, terminal: false },
  { key: 'demo_scheduled', label: 'Demo Scheduled', color: '#F59E0B', weight: 0.3, terminal: false },
  { key: 'onboarding_in_progress', label: 'Onboarding in Progress', color: '#0EA5E9', weight: 0.25, terminal: false },
  { key: 'setup_needed', label: 'Set Up Needed', color: '#8B5CF6', weight: 0.35, terminal: false },
  { key: 'onboarding_complete', label: 'Onboarding Complete', color: '#06B6D4', weight: 0.5, terminal: false },
  { key: 'free_trial_expired', label: 'Free Trial Expired', color: '#F97316', weight: 0.1, terminal: false },
  { key: 'trial_expired_contacted', label: 'Trial Expired — Contacted', color: '#FB923C', weight: 0.15, terminal: false },
  { key: 'trial_reactivated', label: 'Trial Reactivated', color: '#10B981', weight: 0.4, terminal: false },
  { key: 'successfully_signed_up', label: 'Successfully Signed Up', color: '#22C55E', weight: 1.0, terminal: false },
  { key: 'cancelled_subscription', label: 'Cancelled Subscription', color: '#EF4444', weight: 0, terminal: true },
  { key: 'closed_lost', label: 'Closed Lost', color: '#6B7280', weight: 0, terminal: true },
  { key: 'archived', label: 'Archived', color: '#9CA3AF', weight: 0, terminal: true },
]

export const STAGE_MAP: Record<string, Stage> = Object.fromEntries(STAGES.map((s) => [s.key, s]))

/** Max days without activity before warn/breach indicator appears (0 = off). */
export const SLA: Record<string, number> = {
  auto_new_lead: 3,
  qualification: 7,
  demo_scheduled: 2,
  onboarding_in_progress: 10,
  onboarding_complete: 5,
  free_trial_expired: 5,
  trial_reactivated: 7,
}

/* ─── Users / team ────────────────────────────────────────────────────────── */

export interface User {
  id: string
  name: string
  email: string
  role: 'admin' | 'sales_rep'
}

export const USERS: User[] = []
export const ME = USERS[0]
export const userById = (id?: string) => USERS.find((u) => u.id === id)

export const TEAM_ROLES: { id: string; label: string }[] = [
  { id: 'deal_owner', label: 'Deal owner' },
  { id: 'onboarding_lead', label: 'Onboarding lead' },
  { id: 'technical_advisor', label: 'Technical advisor' },
  { id: 'account_manager', label: 'Account manager' },
]
export const teamRoleLabel = (id: string) => TEAM_ROLES.find((r) => r.id === id)?.label || id

/* ─── Companies ───────────────────────────────────────────────────────────── */

export type Country = 'GB' | 'IE' | 'US' | 'AU'
export type CompanySize = 'SMB' | 'Mid' | 'Enterprise'

export interface Company {
  id: string
  name: string
  domain: string
  industry: string
  country: Country
  size: CompanySize
  phone: string
  notes?: string
}

export const COMPANIES: Company[] = []
export const companyById = (id?: string) => COMPANIES.find((c) => c.id === id)

/* ─── Contacts ────────────────────────────────────────────────────────────── */

export type BuyerRole = 'champion' | 'economic_buyer' | 'technical_buyer' | 'influencer'

export interface Contact {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  companyId: string
  title: string
  role: BuyerRole
}

export const BUYER_ROLES: { id: BuyerRole; label: string }[] = [
  { id: 'champion', label: 'Champion' },
  { id: 'economic_buyer', label: 'Economic buyer' },
  { id: 'technical_buyer', label: 'Technical buyer' },
  { id: 'influencer', label: 'Influencer' },
]

export const CONTACTS: Contact[] = []
export const contactById = (id?: string) => CONTACTS.find((c) => c.id === id)
export const contactName = (c?: Contact) => (c ? `${c.firstName} ${c.lastName}` : '')

/* ─── Deal sub-entities ───────────────────────────────────────────────────── */

export type ActivityType =
  | 'system'
  | 'note'
  | 'call'
  | 'email_out'
  | 'email_in'
  | 'email_sync'
  | 'whatsapp'
  | 'demo'
  | 'stage_change'
  // Support ↔ CRM join (Phase 2.1). These are NOT stored on the deal — they are
  // read live from the support tables and merged into the timeline at render time,
  // so a ticket closing in Support is instantly correct on the lead.
  | 'ticket'
  | 'support_chat'

export interface Activity {
  type: ActivityType
  body: string
  who: string
  /** Days ago. */
  at: number
}

export interface Task {
  id: string
  title: string
  done: boolean
  /** Days from now (negative = overdue), or null = no due date. */
  due: number | null
  auto?: boolean
  /** Manual sort order within the deal. */
  order?: number
  /** Part of the seeded default onboarding checklist (vs an ad-hoc/custom task). */
  isDefault?: boolean
  /** One per deal — surfaced on the kanban card as "Next Task". */
  isNext?: boolean
  custom?: boolean
}

/** Default, ordered progress checklist (client fallback when the backend isn't live). */
export const DEFAULT_TASK_TITLES = [
  'Demo booked',
  'Demo completed',
  'Trial started',
  'Key modules set up',
  'Onboarding call',
  'Go-live',
  'Bank details / subscription',
]

export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected'
export interface QuoteLine {
  name: string
  qty: number
  price: number
}
export interface Quote {
  id: string
  num: string
  status: QuoteStatus
  lines: QuoteLine[]
  total: number
}

export interface TeamMember {
  userId: string
  role: string
}

export interface Qual {
  budget?: boolean
  authority?: boolean
  need?: boolean
  timeline?: boolean
  criteria?: boolean
}

export const QUAL_FIELDS: { id: keyof Qual; label: string }[] = [
  { id: 'budget', label: 'Budget confirmed' },
  { id: 'authority', label: 'Authority identified' },
  { id: 'need', label: 'Need confirmed' },
  { id: 'timeline', label: 'Timeline defined' },
  { id: 'criteria', label: 'Decision criteria clear' },
]

export const LOST_REASONS: { id: string; label: string }[] = [
  { id: 'no_budget', label: 'No budget' },
  { id: 'timing', label: 'Wrong timing' },
  { id: 'competitor', label: 'Chose a competitor' },
  { id: 'no_authority', label: 'No decision authority' },
  { id: 'not_a_fit', label: 'Not the right fit' },
  { id: 'dark', label: 'Went dark' },
  { id: 'other', label: 'Other' },
]

export const SOURCES = ['Google Ads', 'Referral', 'Trade Show', 'Calendly', 'WeQuote Trial', 'LinkedIn', 'Partner', 'Direct'] as const

/* ─── Deals ───────────────────────────────────────────────────────────────── */

export interface Deal {
  /** Website-form badge (Lee, 3 Nov 2026): 'trial_requested' | 'demo_pending'. */
  formStatus?: string
  formStatusAt?: string
  /** Fi's PRD (18 Sep 2026): 0–100 qualification gate score + unqualified-demo flag. */
  qualPct?: number
  qualDemoFlag?: { at: string; pct: number | null; reason: string | null }
  id: string
  name: string
  companyId: string
  contactId: string
  /** Denormalised for convenient card/table rendering. */
  company: string
  contact: string
  /** Deal value (£). */
  amount: number
  mrr?: number
  stage: string
  source: string
  /** Lead-import fields (from the mapping tool). */
  opportunity?: string
  leadOwner?: string
  capturedDate?: string
  /** Contacts merged onto this deal (role secondary). */
  secondaryContacts?: Array<{ id: string; name: string; email?: string; title?: string; phone?: string; mobile?: string }>
  country: Country
  /** Server-resolved IANA timezone (state → area code → dial code → country). */
  localTz?: string
  tags: string[]
  /** Assigned CRM flags (shared marker layer). */
  flags?: DealFlag[]
  ownerId: string
  team: TeamMember[]
  /** Lead score 0–100. */
  score: number
  /** Open overdue-task count. */
  overdue?: number
  /** Days ago of last activity. */
  lastActivityDays: number
  /** Days ago the deal was created. */
  logoUrl?: string
  probOverride?: number
  probOverrideBy?: string
  probOverrideReason?: string
  valueEstimated?: boolean
  /** Title of the task marked as next (shown on the kanban card under a divider). */
  nextTask?: string
  createdDays: number
  qual: Qual
  /** Set = archived (soft delete); surfaces offer Unarchive from this. */
  archivedAt?: string
  /** ISO signup moment + discovery gate — the fresh-signup timer badge. */
  trialStartedAt?: string
  discoveryCallDone?: boolean
  /** Primary contact's phone — the To Qualify tab dials this. */
  contactPhone?: string
  /** PRD v1.0 qualification — system-computed on the server, never derived here. */
  qualificationStatus?: 'not_qualified' | 'working' | 'qualified' | 'disqualified' | 'nurture'
  leadGrade?: string
  qualifiedAt?: string
  disqualifyReason?: string
  /** Demo scheduled — days ago (negative = future). */
  demoScheduledDays?: number
  /** Real demo details from Calendly (latest booking). */
  demoAt?: string
  demoEndAt?: string
  demoEventName?: string
  demoMeetUrl?: string
  demoInviteeName?: string
  demoInviteeEmail?: string
  autoCreatedFromDemo?: boolean
  demoToTrialLinked?: boolean
  trialStartedDays?: number
  /** Days-ago of the REAL trial end date from WeQuote (negative = ends in the future). */
  trialEndsDays?: number
  /** Explicit onboarding status from WeQuote events/sync (falls back to stage-derived badge). */
  onboardingStatus?: 'customer_in_progress' | 'customer_finished' | 'wequote_in_progress' | 'wequote_finished'
  /** Days since WeQuote ACTIVATED the account (their date, not when we first saw the lead). */
  activatedDays?: number
  paymentFailed?: boolean
  paymentFailedDays?: number
  paymentFailedAmount?: number
  paymentFailedAttempt?: number
  cardRemoved?: boolean
  cardRemovedDays?: number
  cancelReason?: string
  /** Cancellation request/pending (Lee, 30 Oct 2026) — drive the red/amber banners. */
  cancelRequestedAt?: string | null
  cancelRequestReason?: string | null
  cancelEffectiveAt?: string | null
  lostReason?: string
  closeProb?: number
  /** WeQuote subscription plan name (from lifecycle webhooks). */
  plan?: string
  /** WeQuote SaaS account id (dedupe/link). */
  wequoteAccountId?: string
  /** Google Drive company-folder link — written by the daily CRM↔Drive sync (Lee, 7 Oct 2026). */
  driveFolderUrl?: string
  /** Free-form bag of everything WeQuote sends — onboarding answers, custom fields. */
  wequoteMeta?: Record<string, unknown>
  activities?: Activity[]
  tasks?: Task[]
  quotes?: Quote[]
}

export const DEALS: Deal[] = []
export const dealById = (id?: string) => DEALS.find((d) => d.id === id)

/* ─── Products ────────────────────────────────────────────────────────────── */

export interface Product {
  id: string
  name: string
  price: number
  desc: string
  category: 'subscription' | 'service'
}

export const PRODUCTS: Product[] = [
  { id: 'pr1', name: 'WeQuote Starter — Annual', price: 588, desc: 'Up to 5 users, core quoting', category: 'subscription' },
  { id: 'pr2', name: 'WeQuote Pro — Annual', price: 1788, desc: 'Up to 15 users, full features', category: 'subscription' },
  { id: 'pr3', name: 'WeQuote Enterprise — Annual', price: 3588, desc: 'Unlimited users, white-label', category: 'subscription' },
  { id: 'pr4', name: 'Onboarding & Setup', price: 500, desc: 'Dedicated onboarding specialist', category: 'service' },
  { id: 'pr5', name: 'Priority Support (Annual)', price: 600, desc: '4hr SLA, named account manager', category: 'service' },
  { id: 'pr6', name: 'Custom Integration', price: 1200, desc: 'One bespoke API integration', category: 'service' },
  { id: 'pr7', name: 'Training — Remote (4hrs)', price: 400, desc: 'Live training session for team', category: 'service' },
]

/* ─── Sequences ───────────────────────────────────────────────────────────── */

export interface SequenceStep {
  delay: number
  action: 'email' | 'task' | 'alert'
  tpl: string
  /** A saved email template (wq_crm_email_templates) used by an email step. */
  templateId?: string
}
export interface Sequence {
  id: string
  name: string
  trigger: string
  active: boolean
  steps: SequenceStep[]
}

export const SEQUENCE_TRIGGER_LABELS: Record<string, string> = {
  'stage:auto_new_lead': 'When deal enters Auto New Lead',
  'stage:demo_scheduled': 'When demo scheduled',
  'stage:free_trial_expired': 'When trial expires',
  'stage:qualification': 'When deal enters Qualification',
}

export const SEQUENCES: Sequence[] = []

/* ─── Automations ─────────────────────────────────────────────────────────── */

export interface DealFlag {
  id: string
  name: string
  color: string
}

export interface Automation {
  id: string
  name: string
  active: boolean
  triggerLabel: string
  conditionLabel: string
  actionLabel: string
  runs: number
  trigger?: string
  condition?: string | null
  action?: string
  conditionValue?: string | null
  actionValue?: string | null
  lastRunDays?: number | null
}

export const AUTOMATIONS: Automation[] = []

export const AUTOMATION_TRIGGERS = ['deal_created', 'stage_changed', 'sla_breach', 'payment_failed', 'task_overdue']
export const AUTOMATION_ACTIONS = ['create_task', 'send_email', 'notify_slack', 'assign_owner', 'move_stage']

/* ─── Report builder ──────────────────────────────────────────────────────── */

export interface SavedReport {
  id: string
  name: string
  dimension: string
  metric: string
  chart: string
}

export const SAVED_REPORTS: SavedReport[] = []

export const REPORT_DIMENSIONS = [
  { id: 'source', label: 'Source' },
  { id: 'stage', label: 'Stage' },
  { id: 'owner', label: 'Owner' },
  { id: 'country', label: 'Country' },
]
export const REPORT_METRICS = [
  { id: 'deal_count', label: 'Deal count' },
  { id: 'deal_value', label: 'Deal value' },
  { id: 'mrr', label: 'MRR' },
  { id: 'conv_rate', label: 'Conversion rate' },
  { id: 'avg_age', label: 'Avg deal age' },
]
export const REPORT_CHARTS = [
  { id: 'bar', label: 'Bar' },
  { id: 'line', label: 'Line' },
  { id: 'donut', label: 'Donut' },
  { id: 'table', label: 'Table' },
]

/* ─── Saved views (board) ─────────────────────────────────────────────────── */

export interface SavedView {
  id: string
  name: string
  filters: { mode?: 'all' | 'my'; stage?: string; owner?: string; country?: Country; tags?: string[] }
}

export const SAVED_VIEWS: SavedView[] = []

/* ─── Opportunities (events) ──────────────────────────────────────────────── */

export interface OppGroupType {
  id: string
  label: string
  icon: string
  colour: string
}
export const OPP_GROUP_TYPES: OppGroupType[] = [
  { id: 'trade_show', label: 'Trade show', icon: '🎪', colour: '#F97316' },
  { id: 'conference', label: 'Conference', icon: '🎤', colour: '#7C3AED' },
  { id: 'webinar', label: 'Webinar', icon: '💻', colour: '#0EA5E9' },
  { id: 'networking', label: 'Networking', icon: '🤝', colour: '#10B981' },
  { id: 'referral', label: 'Referral batch', icon: '⭐', colour: '#F59E0B' },
  { id: 'other', label: 'Other', icon: '📋', colour: '#6B7280' },
]
export const oppGroupType = (id: string) => OPP_GROUP_TYPES.find((t) => t.id === id) || OPP_GROUP_TYPES[5]

export interface OppGroup {
  id: string
  name: string
  type: string
  /** Days ago (negative = future). */
  dateDays: number
  /** ISO date (yyyy-mm-dd) — editable. */
  date?: string | null
  location: string
  notes?: string
  cost?: number | null
}
export const OPP_GROUPS: OppGroup[] = []

export type OppStatus = 'new' | 'contacted' | 'converted' | 'dead'
export const OPP_STATUS: Record<OppStatus, { label: string; colour: string; bg: string }> = {
  new: { label: 'New', colour: '#6B7280', bg: '#F1F5F9' },
  contacted: { label: 'Contacted', colour: '#D97706', bg: '#FFFBEB' },
  converted: { label: 'Converted', colour: '#16A34A', bg: '#F0FDF4' },
  dead: { label: 'Not a fit', colour: '#DC2626', bg: '#FEF2F2' },
}

export interface Opp {
  /** Linked deal's CURRENT lifecycle stage (null when the contact never became a lead). */
  dealStage?: string | null
  dealArchived?: boolean
  dealName?: string | null
  /** Linked deal's avatar — WeQuote logo, else uploaded image (7 Sep 2026). */
  avatarUrl?: string | null
  id: string
  groupId: string
  name: string
  company: string
  email: string
  phone: string
  title: string
  status: OppStatus
  notes?: string
  convertedDealId?: string
}
export const OPPS: Opp[] = []

/* ─── Customer-success heartbeat ──────────────────────────────────────────── */

export interface Heartbeat {
  logins_7d: number
  features_used: number
  last_login_days: number
  tickets_open: number
  plan: string
}
export const HEARTBEAT: Record<string, Heartbeat> = {
  d15: { logins_7d: 18, features_used: 9, last_login_days: 0, tickets_open: 0, plan: 'Enterprise' },
  d16: { logins_7d: 4, features_used: 3, last_login_days: 4, tickets_open: 1, plan: 'Pro' },
  d17: { logins_7d: 12, features_used: 6, last_login_days: 1, tickets_open: 0, plan: 'Pro' },
  d18: { logins_7d: 2, features_used: 2, last_login_days: 6, tickets_open: 2, plan: 'Starter' },
  d19: { logins_7d: 8, features_used: 5, last_login_days: 2, tickets_open: 0, plan: 'Pro' },
}

export type ChurnLevel = 'high' | 'med' | 'low'
export function churnRisk(deal: Deal): { score: number; level: ChurnLevel } {
  const hb = HEARTBEAT[deal.id]
  if (!hb) return { score: 50, level: 'med' }
  let s = 100
  if (hb.logins_7d < 3) s -= 30
  else if (hb.logins_7d < 7) s -= 15
  if (hb.features_used < 3) s -= 25
  else if (hb.features_used < 6) s -= 10
  if (hb.last_login_days > 7) s -= 25
  else if (hb.last_login_days > 3) s -= 10
  if (deal.paymentFailed) s -= 20
  if (hb.tickets_open > 1) s -= 15
  s = Math.max(0, Math.min(100, s))
  const level: ChurnLevel = s < 40 ? 'high' : s < 70 ? 'med' : 'low'
  return { score: s, level }
}
export const CHURN_LABEL: Record<ChurnLevel, string> = {
  high: '🔴 High risk',
  med: '🟡 Medium risk',
  low: '🟢 Healthy',
}

/* ─── Role permissions (settings) ─────────────────────────────────────────── */

export interface Perm {
  key: string
  label: string
  enabled: boolean
}
export const PERMS: Perm[] = [
  { key: 'rep_see_all', label: 'Sales reps can see all deals (not just their own)', enabled: false },
  { key: 'rep_delete', label: 'Sales reps can archive/delete deals', enabled: false },
  { key: 'rep_export', label: 'Sales reps can export CSV', enabled: true },
  { key: 'rep_manage_team', label: 'Sales reps can manage team members', enabled: false },
]

export const WEBHOOK_ENDPOINTS = [
  '/api/webhooks/calendly',
  '/api/webhooks/wequote/trial',
  '/api/webhooks/wequote/subscription',
  '/api/webhooks/wequote/payment-failed',
]

/* ─── Derived helpers ─────────────────────────────────────────────────────── */

export const activeDeals = () => DEALS.filter((d) => !STAGE_MAP[d.stage]?.terminal)
export const dealsByStage = (stageKey: string) => DEALS.filter((d) => d.stage === stageKey)

/** Σ value × stage weight, rounded. */
export const weightedForecast = () =>
  Math.round(DEALS.reduce((sum, d) => sum + d.amount * (STAGE_MAP[d.stage]?.weight ?? 0), 0))

/** Σ MRR over signed-up deals. */
export const totalMrr = () =>
  dealsByStage('successfully_signed_up').reduce((sum, d) => sum + (d.mrr || 0), 0)

export type SlaStatus = 'ok' | 'warn' | 'breach'
export function slaStatus(deal: Deal): SlaStatus {
  const limit = SLA[deal.stage]
  if (!limit) return 'ok'
  if (deal.lastActivityDays >= limit) return 'breach'
  if (deal.lastActivityDays >= limit * 0.75) return 'warn'
  return 'ok'
}

/* ─── Formatting helpers ──────────────────────────────────────────────────── */

export const gbp = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`

const CURRENCY_SYMBOL: Record<string, string> = { GB: '£', IE: '€', US: '$', AU: 'A$' }
/** Money formatter. Defaults to £ for any unknown/odd country value WeQuote may
 *  send (e.g. numeric ISO codes) so we never render "undefined". Non-numeric
 *  amounts coerce to 0. */
export const money = (n: number, country: string = 'GB') => {
  const sym = CURRENCY_SYMBOL[country] || '£'
  const amt = Number.isFinite(Number(n)) ? Number(n) : 0
  return `${sym}${Math.round(amt).toLocaleString('en-GB')}`
}

/** Relative time from a "days ago" number. */
export function relTime(days: number): string {
  if (days <= 0) return 'Just now'
  if (days === 1) return 'Yesterday'
  return `${days}d ago`
}

/** Absolute date string from a "days ago" number (negative = future). */
export function dateFromDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Engagement-score colour by threshold (status meaning). */
export function scoreColor(score: number): string {
  if (score >= 70) return '#22c55e'
  if (score >= 40) return '#f59e0b'
  return '#ef4444'
}

/** Deterministic avatar colour from a name (matches the support side). */
export function avatarColor(name: string): string {
  const colors = ['#2563EB', '#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#F97316', '#059669', '#DC2626', '#0284C7']
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

export const flagOf = (c: Country) => ({ GB: '🇬🇧', IE: '🇮🇪', US: '🇺🇸', AU: '🇦🇺' }[c] || '🌍')
